import { useEffect, useRef, useCallback } from 'react';

interface UseInactivityTimerOptions {
    timeout?: number; // Timeout in milliseconds
    onTimeout: () => void;
    enabled?: boolean;
}

/**
 * Hook that triggers a callback after a period of user inactivity
 *
 * @param options.timeout - Inactivity timeout in milliseconds (default: 5 minutes)
 * @param options.onTimeout - Callback to execute when timeout is reached
 * @param options.enabled - Whether the timer is enabled (default: true)
 */
export const useInactivityTimer = ({
    timeout = 5 * 60 * 1000, // 5 minutes default
    onTimeout,
    enabled = true,
}: UseInactivityTimerOptions) => {
    const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
    const onTimeoutRef = useRef(onTimeout);

    // Keep the callback ref up to date
    useEffect(() => {
        onTimeoutRef.current = onTimeout;
    }, [onTimeout]);

    const resetTimer = useCallback(() => {
        // Clear existing timer
        if (timeoutIdRef.current) {
            clearTimeout(timeoutIdRef.current);
        }

        // Set new timer
        if (enabled) {
            timeoutIdRef.current = setTimeout(() => {
                console.log('Inactivity timeout reached');
                onTimeoutRef.current();
            }, timeout);
        }
    }, [timeout, enabled]);

    useEffect(() => {
        if (!enabled) {
            // Clear timer if disabled
            if (timeoutIdRef.current) {
                clearTimeout(timeoutIdRef.current);
                timeoutIdRef.current = null;
            }
            return;
        }

        // Events that indicate user activity
        const events = [
            'mousedown',
            'mousemove',
            'keypress',
            'scroll',
            'touchstart',
            'click',
        ];

        // Start initial timer
        console.log(
            `Inactivity timer started (${timeout / 1000}s timeout, enabled: ${enabled})`
        );
        resetTimer();

        // Add event listeners
        events.forEach((event) => {
            document.addEventListener(event, resetTimer, true);
        });

        // Cleanup
        return () => {
            if (timeoutIdRef.current) {
                clearTimeout(timeoutIdRef.current);
            }
            events.forEach((event) => {
                document.removeEventListener(event, resetTimer, true);
            });
        };
    }, [resetTimer, enabled]);
};
